import React, { useEffect, useState } from "react";
import { Heading, Flex, Card, Button, Text, Grid, Theme, Box, Badge } from "@radix-ui/themes";
import { createThemePreferencesProperties, deleteThemePreferencesProperties, updateThemePreferencesProperties } from "./ThemePreferenceProperties";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useForm, useWatch } from "react-hook-form";
import { useThemePreferencesProperties } from "./UseThemePreferences";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import toast from "react-hot-toast";


export const ColorOptions = [
  { id: "gray", description: "Cinza" }, { id: "gold", description: "Dourado" }, { id: "bronze", description: "Bronze" },
  { id: "brown", description: "Castanho" }, { id: "yellow", description: "Amarelo" }, { id: "amber", description: "Âmbar" },
  { id: "orange", description: "Laranja" }, { id: "tomato", description: "Tomate" }, { id: "red", description: "Vermelho" },
  { id: "ruby", description: "Rubi" }, { id: "crimson", description: "Carmesim" }, { id: "pink", description: "Rosa" },
  { id: "plum", description: "Ameixa" }, { id: "purple", description: "Roxo" }, { id: "violet", description: "Violeta" },
  { id: "iris", description: "Íris" }, { id: "indigo", description: "Índigo" }, { id: "blue", description: "Azul" },
  { id: "cyan", description: "Ciano" }, { id: "teal", description: "Verde-azulado" }, { id: "jade", description: "Jade" },
  { id: "green", description: "Verde" }, { id: "grass", description: "Verde relva" }, { id: "lime", description: "Lima" },
  { id: "mint", description: "Menta" }, { id: "sky", description: "Celeste" },
] as const;

export const GrayOptions = [
  { id: "auto", description: "Automático" }, { id: "gray", description: "Padrão" }, { id: "mauve", description: "Malva" },
  { id: "slate", description: "Ardósia" }, { id: "sage", description: "Sálvia" }, { id: "olive", description: "Oliva" },
  { id: "sand", description: "Areia" },
] as const;

export const RadiusOptions = [
  { id: "none", description: "Sem bordas" }, { id: "small", description: "Pequeno" },
  { id: "medium", description: "Médio" }, { id: "large", description: "Grande" }, { id: "full", description: "Completo" },
] as const;

const DEFAULTS = {
  appearance: "",
  accentColor: "",
  grayColor: "",
  radius: "",
  scaling: "",
};

export const ThemeOptionsScreen = ({ userId }: { userId?: string }) => {
  const { data: themeData, isLoading, refetch } = useThemePreferencesProperties();
  const [saveAction, setSaveAction] = useState<any>(null);
  const [updateAction, setUpdateAction] = useState<any>(null);
  const [removeAction, setRemoveAction] = useState<any>(null);
  
  const { control, formState: { errors }, reset, watch } = useForm({
    defaultValues: DEFAULTS
  });
const previewTheme = useWatch({ control });


  useEffect(() => {
    if (themeData) {
      reset({
        appearance: themeData.appearance || DEFAULTS.appearance,
        accentColor: themeData.accentColor || DEFAULTS.accentColor,
        grayColor: themeData.grayColor || DEFAULTS.grayColor,
        radius: themeData.radius || DEFAULTS.radius,
        scaling: themeData.scaling || DEFAULTS.scaling,
      });
    }
  }, [themeData, reset]);

  
  const handleSave = () => {
    const formValues = watch();
    
    if (themeData?.id) {
      setUpdateAction(formValues);
    } else {
      setSaveAction(formValues);
    }
  };

  
  const confirmSave = async () => {
    try {
      const body = { userId: userId!, ...saveAction };
      await createThemePreferencesProperties(body);
      await refetch();
      setSaveAction(null);
      toast.success("Tema guardado com sucesso!");
    } catch (e: any) {
      toast.error(`Erro ao guardar: ${e?.message ?? e}`);
    }
  };

  
  const confirmUpdate = async () => {
    if (!themeData?.id) return;
    
    try {
      const body = { userId: userId!, ...updateAction };
      await updateThemePreferencesProperties(themeData.id, body);
      await refetch();
      setUpdateAction(null);
      toast.success("Tema atualizado com sucesso!");
    } catch (e: any) {
      toast.error(`Erro ao atualizar: ${e?.message ?? e}`);
    }
  };


  const handleDelete = () => {
    if (!themeData?.id) return;
    setRemoveAction(themeData);
  };


  const confirmRemove = async () => {
    try {
      await deleteThemePreferencesProperties(removeAction.id);
      reset(DEFAULTS);
      await refetch();
      setRemoveAction(null);
      toast.success("Preferências removidas com sucesso!");
    } catch (e: any) {
      toast.error(`Erro ao remover: ${e?.message ?? e}`);
    }
  };

  if (isLoading) return <Text>A processar...</Text>;

  return (
    <>
      <Flex direction="column" align="center" gap="4" p="6">
        <Heading size="6" mb="2">Configurações de Tema</Heading>
        
        <Grid columns="2" gap="6" style={{ maxWidth: 1000, width: '100%' }}>
         
            <Card style={{ padding: 24, height: '100%' }}>
            <Heading size="4" mb="4">Personalizar</Heading>
            
            <SelectComponent
              required={true}
              name="appearance"
              label="Aparência"
              control={control}
              errors={errors}
              options={[
                { value: "inherit", label: "Herdar" },
                { value: "light", label: "Claro" },
                { value: "dark", label: "Escuro" }
              ]}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="accentColor"
              label="Cor de Destaque"
              control={control}
              errors={errors}
              options={ColorOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="grayColor"
              label="Tons de Cinzento"
              control={control}
              errors={errors}
              options={GrayOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="radius"
              label="Bordas"
              control={control}
              errors={errors}
              options={RadiusOptions.map(o => ({ value: o.id, label: o.description }))}
              style={{ width: '100%' }}
            />
            
            <SelectComponent
              required={true}
              name="scaling"
              label="Escala"
              control={control}
              errors={errors}
              options={[
                { value: "90%", label: "90%" },
                { value: "95%", label: "95%" },
                { value: "100%", label: "100%" },
                { value: "105%", label: "105%" },
                { value: "110%", label: "110%" }
              ]}
              style={{ width: '100%' }}
            />
            
            <Flex justify="between" mt="5" gap="3">
              <Button 
                color={themeData?.id ? "blue" : "green"} 
                onClick={handleSave}
              >
                {themeData?.id ? "Atualizar" : "Guardar"}
              </Button>
              <Button 
                color="red" 
                variant="soft" 
                onClick={handleDelete} 
                disabled={!themeData?.id}
              >
                Remover
              </Button>
            </Flex>
          </Card>

          
          <Theme 
            appearance={previewTheme.appearance as any || "light"}
            accentColor={previewTheme.accentColor as any || "indigo"}
            grayColor={previewTheme.grayColor as any || "gray"}
            radius={previewTheme.radius as any || "medium"}
            scaling={previewTheme.scaling as any || "100%"}
          >
            <Card style={{ padding: 24, height: '100%' }}>
              <Heading size="4" mb="4">Pré-visualização</Heading>
              
              <Flex direction="column" gap="4">
                <Box>
                  <Text size="2" weight="bold" mb="2" style={{ display: 'block' }}>
                    Aparência: {previewTheme.appearance || "light"}
                  </Text>
                  <Text>
                    Este é um exemplo de texto normal no tema selecionado. 
                    Você pode ver como as cores e estilos aparecem.
                  </Text>
                </Box>

              
                <Flex gap="2" wrap="wrap">
                  <Button size="2">Botão Primário</Button>
                  <Button size="2" variant="soft">Botão Suave</Button>
                  <Button size="2" variant="outline">Botão Outline</Button>
                </Flex>

                <Card style={{ padding: 16, backgroundColor: 'var(--gray-3)' }}>
                  <Text size="2" weight="bold" mb="2" style={{ display: 'block' }}>
                    Card Aninhado
                  </Text>
                  <Text size="1">
                    Cards e containers usam os tons de cinza selecionados.
                  </Text>
                </Card>

                <Flex direction="column" gap="2">
                  <Text size="1" color="gray">Texto secundário em cinzento</Text>
                  <Text size="3" weight="bold">Texto em destaque</Text>
                  <Text size="2">Texto de tamanho médio</Text>
                </Flex>

                <Box style={{ 
                  padding: 14, 
                  border: '2px solid var(--accent-9)',
                  borderRadius: 'var(--radius-3)'
                }}>
                  <Text size="2">
                    Bordas com raio: <strong>{previewTheme.radius || "medium"}</strong>
                  </Text>
                </Box>

                <Flex gap="2" align="center">
                  <Button size="1" variant="soft">Pequeno</Button>
                  <Button size="2" variant="soft">Médio</Button>
                  <Button size="3" variant="soft">Grande</Button>
                </Flex>
              </Flex>
            </Card>
          </Theme>
        </Grid>
      </Flex>

      <ConfirmAlertDialog
        open={!!saveAction}
        onOpenChange={(open: any) => !open && setSaveAction(null)}
        title="Guardar configuração"
        item={saveAction}
        onConfirm={confirmSave}
        onCancel={() => setSaveAction(null)}
      />

      <ConfirmAlertDialog
        open={!!updateAction}
        onOpenChange={(open: any) => !open && setUpdateAction(null)}
        title="Atualizar configuração"
        item={updateAction}
        onConfirm={confirmUpdate}
        onCancel={() => setUpdateAction(null)}
      />

      <ConfirmAlertDialog
        open={!!removeAction}
        onOpenChange={(open: any) => !open && setRemoveAction(null)}
        title="Remover configuração"
        item={removeAction}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveAction(null)}
      />
    </>
  );
};